import { chain, find, isEmpty, pick, some } from 'underscore';

/*
 * Captures:
 * [0]: whole match
 * [1]: rsa ad customizer attribute name
 * [2]: default value
 */
const RSAAdCustomizerRuleSyntax = /\{customizer.([^{}():/"'“‘$*-+=[\]\\:,.]*)(?::?}|:([^{}]+)})/i;

/*
 * Captures:
 * [0]: whole match
 * [1]: rsa ad customizer attribute name (could be empty)
 * [2]: default value (could be empty)
 */
const RSAAdCustomizerRuleInProgressSyntax = /\{c(?:u|$)(?:s|$)(?:t|$)(?:o|$)(?:m|$)(?:i|$)(?:z|$)(?:e|$)(?:r|$)(?:\.|$)([^{}():/"'“‘$*-+=[\]\\:,.]*)(?::|$)([^{}]*)/i;

/**
 * Looks for rsa ad customizer rules in given text and substitutes them with their values.
 * {rsa ad customizer.ProductName:CocaCola} => CocaCola
 * @param {string} text - input text
 * @param {Array} attributes - attributes
 * @returns {string} text after substitutions
 */
export function evaluateRSAAdCustomizerRules(text, attributes) {
  if (text && text.length > 0) {
    const RSAAdCustomizerReplaceSyntax = new RegExp(RSAAdCustomizerRuleSyntax.source, 'gi');

    return text.replace(RSAAdCustomizerReplaceSyntax, (match, p1, p2) => {
      const matchedAttribute = find(attributes, attribute => attribute.Name === p1);
      if (matchedAttribute) {
        const values = chain([matchedAttribute.AdGroupValue, matchedAttribute.CampaignValue, matchedAttribute.AccountValue])
          .compact()
          .value();
        if (!isEmpty(values)) {
          return values[0];
        }
      }
      if (p2) return p2;
      return match;
    });
  }
  return text;
}

/**
 * Looks for a rsa ad customizer rule definition in given text starting from passed index.
 *   If found, it parses the definition.
 * @param {string} text - text to search in
 * @param {string} syntax - regex syntax use for matching
 * @param {int} startIndex - starts searching at this index
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} parses rule definition properties or undefined if a definiton is not found.
 */
export function parseRSAAdCustomizerRule(text, syntax, startIndex, cursorIndex) {
  const tokens = syntax.exec(text);

  if ((tokens === null) ||
    (tokens.length !== 3) ||
    (cursorIndex && cursorIndex <= (startIndex + tokens.index))) {
    return null;
  }

  return {
    source: tokens[0],
    defaultText: tokens[2],
    attributeName: tokens[1],
  };
}

/**
 * Parses out properties to initialize a rsa ad customizer helper using current cursor position
 * @param {string} text - input text
 * @param {int} cursorIndex - current cursor position
 * @returns {Object} properties to initialize a rsa ad customizer helper
 *   if cursor is placed within a valid definition or at the end of a partial definition.
 * Returns undefined; otherwise.
 */
export function parseRule(text, cursorIndex) {
  const processedText = text || '';
  const startIndex = processedText.lastIndexOf('{', cursorIndex);

  if (startIndex === -1 || cursorIndex === startIndex) {
    return null;
  }

  const parsers = [
    {
      input: processedText.substring(startIndex),
      syntax: RSAAdCustomizerRuleSyntax,
      inclusive: false,
    },
    {
      input: processedText.slice(startIndex, cursorIndex),
      syntax: RSAAdCustomizerRuleInProgressSyntax,
      inclusive: true,
    },
  ];

  let properties = null;

  some(parsers, (parser) => {
    const rule = parseRSAAdCustomizerRule(parser.input, parser.syntax, startIndex, cursorIndex);

    if ((rule !== null) &&
      (cursorIndex < startIndex + rule.source.length + (parser.inclusive ? 1 : 0))) {
      const options = pick(rule, 'defaultText', 'attributeName');

      options.prefix = processedText.substring(0, startIndex);
      options.suffix = processedText.substring(startIndex + rule.source.length);
      options.startIndex = startIndex;
      options.endIndex = startIndex + rule.source.length;

      properties = {
        type: 'RSAAdCustomizer',
        options,
      };

      return true;
    }
    return false;
  });

  return properties;
}
